import {getTestProps} from '@benchling/js-test-utils';
import {Box, Center, Loader, Tabs, Text, createStyles} from '@mantine/core';
import React, {useCallback, useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {setWindowTitle} from 'BenchlingUtil';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';

import BatchDetailPage from './BatchDetailPage';
import BatchesPage from './BatchesPage';
import DetailBreadcrumbs from './lib/DetailBreadcrumbs';
import type {DetailCrumb} from './lib/detailCrumbs';
import ModelHubContent from './ModelHubContent';
import ModelHubInferenceBanner from './ModelHubInferenceBanner';
import type {ModelHubPage} from './ModelHubPages';
import {
  batchesPath,
  modelsPath,
  pathForPage,
  pathForRoute,
  predictionsPath,
  type ModelHubRoute,
} from './modelHubRoutes';
import NewPredictionPage from './new-prediction/NewPredictionPage';
import PredictionDetailPage from './PredictionDetailPage';
import PredictionsPage from './PredictionsPage';
import {requireScientificModel, type ScientificModelNode} from './scientific-models/ScientificModelMetadata';
import {
  type ScientificModelRegistry,
  useScientificModelRegistry,
} from './scientific-models/useAvailableScientificModels';

export type CoreModelHubRoute = Extract<
  ModelHubRoute,
  {kind: 'models' | 'predictions' | 'batches' | 'new-prediction' | 'prediction-detail' | 'batch-detail'}
>;

interface CoreModelHubViewProps {
  route: CoreModelHubRoute;
}

export default function CoreModelHubView({route}: CoreModelHubViewProps): JSX.Element {
  const result = useScientificModelRegistry();
  // These replace the whole view, so they fill the host region. That region is shorter than the
  // viewport in core, hence `100%` rather than `100vh`.
  if (result.type === 'loading') {
    return (
      <Center h="100%">
        <Loader size="md" />
      </Center>
    );
  }
  if (result.type === 'error') {
    return (
      <Center h="100%">
        <Text color="red">Error loading models: {result.error.message}</Text>
      </Center>
    );
  }
  return <LoadedCoreModelHubView registry={result.registry} route={route} />;
}

function LoadedCoreModelHubView({
  registry,
  route,
}: {
  registry: ScientificModelRegistry;
  route: CoreModelHubRoute;
}): JSX.Element {
  const {classes} = useStyles();

  // Fires once per visit rather than per tab, since navigating between tabs re-renders this component
  // instead of remounting it.
  useEffect(() => {
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.MODEL_HUB_VIEWED);
    setWindowTitle('Model Hub');
  }, []);

  const handleNavigate = useCallback((page: ModelHubPage) => {
    navigateToURL(pathForPage(page));
  }, []);
  const handleNewPrediction = useCallback((model?: ScientificModelNode) => {
    navigateToURL(
      pathForRoute(
        model == null ? {kind: 'new-prediction'} : {kind: 'new-prediction', model: model.externalReference}
      )
    );
  }, []);
  const handleSelectBatch = useCallback((batchId: string) => {
    navigateToURL(pathForRoute({kind: 'batch-detail', id: batchId}));
  }, []);
  const handleSelectPrediction = useCallback((predictionId: string) => {
    navigateToURL(pathForRoute({kind: 'prediction-detail', id: predictionId}));
  }, []);
  const handleBackFromNewPrediction = useCallback(() => {
    navigateToURL(pathForPage('models'));
  }, []);
  const handleNewPredictionSubmitted = useCallback(() => {
    navigateToURL(pathForPage('batches'));
  }, []);
  const handleBackToBatches = useCallback(() => {
    navigateToURL(batchesPath());
  }, []);
  // The batch filter is a query param rather than part of the route, so this builds the path directly —
  // `pathForRoute({kind: 'predictions'})` would drop the filter.
  const handleViewPredictions = useCallback((batchId: string) => {
    navigateToURL(predictionsPath(batchId));
  }, []);
  const handleNavigateToPredictions = useCallback(() => {
    navigateToURL(predictionsPath());
  }, []);

  // Detail pages hand up their own trail so the chrome can render one continuous hierarchy instead of
  // stacking a second breadcrumb row inside the page. The trail is tagged with the route it describes and
  // read back only for a matching route, so a previous page's crumbs never show, even on the render before
  // the clearing effect below runs.
  const routeKey = 'id' in route ? `${route.kind}:${route.id}` : route.kind;
  const [reportedCrumbs, setReportedCrumbs] = useState<{
    routeKey: string;
    crumbs: ReadonlyArray<DetailCrumb>;
  } | null>(null);
  const handleCrumbsChange = useCallback(
    (crumbs: ReadonlyArray<DetailCrumb>) => {
      setReportedCrumbs({crumbs, routeKey});
    },
    [routeKey]
  );
  // Drops a report for any other route, so returning to a page doesn't reuse its earlier trail while it
  // reloads. The functional update keeps a report the new page has already made: child effects run first.
  useEffect(() => {
    setReportedCrumbs((previous) => (previous?.routeKey === routeKey ? previous : null));
  }, [routeKey]);
  const detailCrumbs = reportedCrumbs?.routeKey === routeKey ? reportedCrumbs.crumbs : [];

  if (route.kind === 'new-prediction') {
    return (
      <NewPredictionPage
        availableModels={registry.availableStructurePredictionModels}
        onBack={handleBackFromNewPrediction}
        onSubmitted={handleNewPredictionSubmitted}
        preselectedModel={
          route.model == null
            ? undefined
            : requireScientificModel(registry.catalogByExternalReference, route.model)
        }
        withFolderPicker={true}
        withRegistryInputs={true}
      />
    );
  }

  const rootCrumb: DetailCrumb = {
    label: 'Model Hub',
    href: modelsPath(),
    onClick: () => {
      handleNavigate('models');
    },
  };

  return (
    <Box className={classes.container} {...getTestProps({component: 'CoreModelHubView', element: 'root'})}>
      {route.kind === 'batch-detail' || route.kind === 'prediction-detail' ? (
        <>
          <Box className={classes.titleRow}>
            {/* Small type throughout, since the detail page renders its own title for the current crumb. */}
            <DetailBreadcrumbs crumbs={[rootCrumb, ...detailCrumbs]} />
          </Box>
          {/* Detail pages sit below every tab, so the trail alone locates them. */}
          <Box className={classes.tabPanel}>
            {route.kind === 'batch-detail' && (
              <BatchDetailPage
                batchId={route.id}
                catalogByExternalReference={registry.catalogByExternalReference}
                onBackToBatches={handleBackToBatches}
                onCrumbsChange={handleCrumbsChange}
                onSelectPrediction={handleSelectPrediction}
                onViewPredictions={handleViewPredictions}
              />
            )}
            {route.kind === 'prediction-detail' && (
              <PredictionDetailPage
                catalogByExternalReference={registry.catalogByExternalReference}
                onCrumbsChange={handleCrumbsChange}
                onNavigateToBatch={handleSelectBatch}
                onNavigateToBatches={handleBackToBatches}
                onNavigateToPredictions={handleNavigateToPredictions}
                predictionId={route.id}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box className={classes.titleRow}>
            <Text size="xl">Model Hub</Text>
          </Box>
          <Tabs
            classNames={{root: classes.tabs, panel: classes.tabPanel}}
            onTabChange={(page) => {
              handleNavigate(page as ModelHubPage);
            }}
            value={route.kind}
          >
            <Tabs.List>
              <Tabs.Tab value="models">Models</Tabs.Tab>
              <Tabs.Tab value="predictions">Predictions</Tabs.Tab>
              <Tabs.Tab value="batches">Batches</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value={route.kind}>
              {route.kind === 'models' && (
                <ModelHubContent
                  models={registry.availableModels}
                  onNewPrediction={handleNewPrediction}
                  withTitle={false}
                />
              )}
              {route.kind === 'predictions' && (
                <PredictionsPage
                  catalogByExternalReference={registry.catalogByExternalReference}
                  models={registry.availableModels}
                  onNewPrediction={handleNewPrediction}
                  onSelectPrediction={handleSelectPrediction}
                  withTitle={false}
                />
              )}
              {route.kind === 'batches' && (
                <BatchesPage
                  catalogByExternalReference={registry.catalogByExternalReference}
                  onNewPrediction={handleNewPrediction}
                  onSelectBatch={handleSelectBatch}
                  withTitle={false}
                />
              )}
            </Tabs.Panel>
          </Tabs>
        </>
      )}
      <Box
        className={classes.bannerWrapper}
        {...getTestProps({component: 'CoreModelHubView', element: 'inferenceBannerSlot'})}
      >
        <ModelHubInferenceBanner />
      </Box>
    </Box>
  );
}

// Core's full-screen views inset their content past the global nav sidebar.
const NAV_INSET_PX = 90;

const useStyles = createStyles((theme) => ({
  container: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    padding: `${theme.spacing.lg} ${theme.spacing.xxl} 0 ${NAV_INSET_PX}px`,
    width: '100%',
  },
  bannerWrapper: {
    flex: '0 0 auto',
    marginRight: NAV_INSET_PX,
    position: 'relative',
    zIndex: 100,

    '&:not(:empty)': {
      paddingTop: theme.spacing.lg,
    },
  },
  // The single scroll owner for pages that grow. Pages that instead bound themselves to `height: 100%`
  // and scroll an inner region fit here exactly, so either way there is one scrollbar — but only as long
  // as no page forces a minimum height. A viewport-relative minimum is taller than this region, which
  // both makes this scroll unconditionally and adds a second scrollbar next to the page's own.
  // The negative left margin cancels the container's nav inset and the matching padding puts it back
  // inside, so the wheel scrolls over the inset too. The bottom padding keeps the last section clear of
  // the viewport edge.
  tabPanel: {
    flex: '1 1 auto',
    marginLeft: -NAV_INSET_PX,
    marginTop: theme.spacing.md,
    minHeight: 0,
    overflow: 'auto',
    paddingBottom: theme.spacing.xl,
    paddingLeft: NAV_INSET_PX,
  },
  // Lays the list and its panel out as a column, so the panel takes the remaining height.
  tabs: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    minHeight: 0,
  },
  titleRow: {
    flex: '0 0 auto',
    marginBottom: theme.spacing.lg,
  },
}));
